/**
 * Willingness to Reassign Gates Enforcement
 * Validates cross-tech reassignments based on shop culture settings
 */

import {
  calculateProfGapMax,
  calculateEarlierByMin,
  calculateDontStealWindow,
  DEBUG_WILLINGNESS_GATES,
} from '@/constants/schedulingConstants';
// DTC-C: optional shop-tz path for the don't-steal-window comparison.
// When `shopTimezone` is supplied on the context, ticketLastMoveTime is
// converted to shop-local minutes via `getMinutesInShopTZ` instead of
// the host-TZ getHours/getMinutes contract. The legacy contract path
// is preserved verbatim for existing callers that already pre-zone.
import { getMinutesInShopTZ } from '@/utils/timezoneMath';

export interface WillingnessGates {
  profGapMax: number;       // Maximum proficiency gap allowed (5-20%)
  earlierByMin: number;     // Minimum time gain required (60-180 min)
  dontStealWindow: number;  // Protection window for recently moved tickets (60-120 min)
  /**
   * CORE-BI: the willingness value these gates were actually derived from,
   * after safety correction. This is the operator-facing attribution of
   * preference influence — a shop can be told WHICH governing value produced
   * a threshold, not merely what the threshold was.
   */
  governingWillingness: number;
  /**
   * CORE-BI: true when the supplied willingness was unusable and the safest
   * value was substituted. A shop must be able to see that its stated
   * preference was NOT the one in force.
   */
  correctedFromUnsafeValue: boolean;
}

/**
 * CORE-BI — the safest willingness. Zero yields the most restrictive gates
 * (profGapMax 5%, earlierByMin 180min, dontStealWindow 120min), so an
 * unusable culture value protects work rather than exposing it.
 */
const SAFEST_WILLINGNESS = 0;
const MIN_WILLINGNESS = 0;
const MAX_WILLINGNESS = 100;

/**
 * Calculate all Willingness gates from shop's willingness_to_reassign value.
 *
 * CORE-BI — UNSAFE VALUES MUST FAIL CLOSED.
 *
 * The underlying formulas are unclamped linear functions. Fed a value outside
 * 0-100 they produce gates that do not merely misbehave, they INVERT:
 *
 *   willingness = 1000  ->  earlierByMin = -1020, dontStealWindow = -480
 *                           Gate 2 and Gate 3 can never block anything.
 *   willingness = NaN   ->  every gate is NaN, and every comparison against
 *                           NaN is false, so ALL THREE gates pass silently.
 *
 * In both cases a bad culture value becomes a blanket bypass of the shop's
 * own reassignment protection. Culture may loosen a gate only as far as the
 * shop legitimately chose; it may never disable the gate by accident.
 *
 * @param willingness - Shop's willingness_to_reassign (0-100)
 * @returns Calculated gates, with the governing value attributed
 */
export function calculateWillingnessGates(willingness: number): WillingnessGates {
  const usable = typeof willingness === 'number' && Number.isFinite(willingness);
  const corrected = !usable || willingness < MIN_WILLINGNESS || willingness > MAX_WILLINGNESS;
  const governing = !usable
    ? SAFEST_WILLINGNESS
    : Math.min(MAX_WILLINGNESS, Math.max(MIN_WILLINGNESS, willingness));

  const gates = {
    profGapMax: calculateProfGapMax(governing),
    earlierByMin: calculateEarlierByMin(governing),
    dontStealWindow: calculateDontStealWindow(governing),
    governingWillingness: governing,
    correctedFromUnsafeValue: corrected,
  };

  if (DEBUG_WILLINGNESS_GATES) {
    console.log(`[Willingness Gates] w=${willingness}%:`, gates);
  }

  return gates;
}

export interface ReassignmentContext {
  currentTechTPS: number;           // Current tech's TPS for this ticket
  proposedTechTPS: number;          // Proposed tech's TPS for this ticket
  currentStartMin: number;          // Current start time (minutes from midnight)
  proposedStartMin: number;         // Proposed start time (minutes from midnight)
  ticketLastMoveTime?: Date;        // When ticket was last manually moved (if ever)
  currentTimeMin: number;           // Current time (minutes from midnight)
  /**
   * DTC-C: optional shop timezone. When provided, `ticketLastMoveTime`
   * is interpreted as a UTC instant and converted to shop-local
   * minutes-since-midnight via `getMinutesInShopTZ`. When omitted, the
   * pre-DTC-C contract applies — the caller MUST pre-convert the Date
   * via `toZonedTime(rawUtc, shopTz)` so its host-TZ getHours/getMinutes
   * align with `currentTimeMin`. Edge-mirror callers (smart-assign,
   * crossTechEvaluator) currently honor the latter contract; new
   * browser callers should prefer this field to make the TZ frame
   * explicit at the type level.
   */
  shopTimezone?: string;
}

/**
 * Check if cross-tech reassignment passes Willingness gates
 * @param context - Reassignment context (TPS scores, timing)
 * @param gates - Calculated Willingness gates
 * @returns { allowed: boolean, reason?: string }
 */
export function checkReassignmentGates(
  context: ReassignmentContext,
  gates: WillingnessGates
): { allowed: boolean; reason?: string } {
  const {
    currentTechTPS,
    proposedTechTPS,
    currentStartMin,
    proposedStartMin,
    ticketLastMoveTime,
    currentTimeMin,
    shopTimezone,
  } = context;

  // Gate 1: Proficiency Gap Check
  const profGap = Math.abs(proposedTechTPS - currentTechTPS);
  if (profGap > gates.profGapMax) {
    if (DEBUG_WILLINGNESS_GATES) {
      console.log(`[Willingness Gates] BLOCKED: Prof gap ${profGap.toFixed(1)}% > max ${gates.profGapMax.toFixed(1)}%`);
    }
    return {
      allowed: false,
      reason: `Proficiency gap ${profGap.toFixed(1)}% exceeds max ${gates.profGapMax.toFixed(1)}%`,
    };
  }

  // Gate 2: Earlier-By-Min Check (must start earlier by threshold)
  const timeGainMin = currentStartMin - proposedStartMin;
  if (timeGainMin < gates.earlierByMin) {
    if (DEBUG_WILLINGNESS_GATES) {
      console.log(`[Willingness Gates] BLOCKED: Time gain ${timeGainMin} min < min ${gates.earlierByMin} min`);
    }
    return {
      allowed: false,
      reason: `Time gain ${timeGainMin} min below required ${gates.earlierByMin} min`,
    };
  }

  // Gate 3: Don't-Steal Window (protect recently moved tickets)
  if (ticketLastMoveTime) {
    // DTC-C: prefer canonical shop-tz minute extraction when the
    // caller supplies `shopTimezone`. Otherwise fall back to the
    // pre-DTC-C contract — the host-TZ getHours/getMinutes pattern
    // — which production edge-mirror callers (smart-assign,
    // crossTechEvaluator) already honor by pre-converting via
    // `toZonedTime(rawUtcDate, SHOP_TZ)`. Behavior unchanged for
    // existing callers; future browser callers should pass
    // `shopTimezone` to make the TZ frame explicit.
    const lastMoveMin = shopTimezone
      ? getMinutesInShopTZ(ticketLastMoveTime.toISOString(), shopTimezone)
      : ticketLastMoveTime.getHours() * 60 + ticketLastMoveTime.getMinutes();
    const minutesSinceMove = currentTimeMin - lastMoveMin;

    if (minutesSinceMove < gates.dontStealWindow) {
      if (DEBUG_WILLINGNESS_GATES) {
        console.log(
          `[Willingness Gates] BLOCKED: ${minutesSinceMove} min since last move < protection window ${gates.dontStealWindow} min`
        );
      }
      return {
        allowed: false,
        reason: `Ticket in protection window (${minutesSinceMove} min < ${gates.dontStealWindow} min)`,
      };
    }
  }

  // All gates passed
  if (DEBUG_WILLINGNESS_GATES) {
    console.log(
      `[Willingness Gates] ALLOWED: Prof gap ${profGap.toFixed(1)}% ≤ ${gates.profGapMax.toFixed(1)}%, ` +
      `Time gain ${timeGainMin} min ≥ ${gates.earlierByMin} min`
    );
  }

  return { allowed: true };
}
